import classNames from "classnames";
import React, {
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useId,
  useState,
} from "react";

import { SVVAlert } from "@komponentkassen/svv-alert";
import { SVVFormError } from "./FormError";
import { SVVLabel } from "./Label";
import { Description } from "./Description";

type TextInputProps = {
  label: string;
  className?: string;
  errorMessage?: string;
  inputSize?: "xsmall" | "small" | "medium" | "large";
  id: string;
  required?: boolean;
  maxLength?: number;
  maxLengthHelperText?: string;
  maxLengthWarning?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  popoverText?: string;
  popoverTitle?: string;
  removeMargin?: boolean;
  isFullWidth?: boolean;
  description?: string;
  showWarning?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

function Component(
  {
    className,
    errorMessage,
    label,
    placeholder,
    inputSize = "medium",
    id,
    required,
    type = "text",
    maxLength,
    maxLengthHelperText = "maks antall tegn er ",
    maxLengthWarning = " tegn igjen til maks er nådd.",
    value,
    onChange,
    popoverText,
    popoverTitle,
    removeMargin = false,
    isFullWidth = false,
    description,
    showWarning = false,
    ...rest
  }: TextInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const [errorMessageId] = useState(`feilmelding-${useId()}`);
  const hasErrorMessage = !!errorMessage;
  const maxLengthLabel = maxLength ? `(${maxLengthHelperText} ${maxLength})` : "";
  const warningNumber = maxLength ? maxLength - 10 : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
  };

  useEffect(() => {}, [ref]);
  return (
    <div
      className={classNames(
        "svv-form-element",
        {
          "svv-form-element--margin-bottom": !removeMargin,
          "svv-form-element--max-width": !isFullWidth,
        },
        className,
      )}
    >
      {label && (
        <SVVLabel
          htmlFor={id}
          required={required}
          popoverText={popoverText}
          popoverTitle={popoverTitle}
        >
          {label} {maxLengthLabel}
        </SVVLabel>
      )}
      {description && <Description content={description} />}
      <input
        id={id}
        className={classNames("svv-form-input__text", {
          "svv-form-input__text--error": hasErrorMessage,
          "input--x-small": inputSize === "xsmall",
          "input--small": inputSize === "small",
          "input--medium": inputSize === "medium",
          "input--large": inputSize === "large",
        })}
        placeholder={placeholder}
        ref={ref}
        aria-invalid={hasErrorMessage}
        aria-describedby={hasErrorMessage ? errorMessageId : undefined}
        required={required}
        type={type}
        value={value}
        onChange={handleChange}
        {...rest}
      />
      {maxLengthHelperText && maxLength && (
        <div>
          <p>
            {value.length}/{maxLength}
          </p>
        </div>
      )}

      <div aria-live="assertive">
        {errorMessage && (
          <SVVFormError id={errorMessageId}>
            <p>{errorMessage}</p>
          </SVVFormError>
        )}
      </div>
      <div>
        {showWarning &&
          warningNumber &&
          maxLength &&
          !errorMessage &&
          value.length > maxLength - 10 && (
            <SVVAlert type="info">
              {maxLength - value.length} {maxLengthWarning}
            </SVVAlert>
          )}
      </div>
    </div>
  );
}

export const SVVTextInput = forwardRef<HTMLInputElement, TextInputProps>(Component);

SVVTextInput.displayName = "SVVTextInput";
